import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import axiosRetry from 'axios-retry'
import dayjs from 'dayjs'
import Logger from 'services/Logger'
import { LocalStorageKey } from 'types/services/localStorage'
import { isPermissionOrSubscriptionError } from 'utils/api/codes'
import { getAccessToken } from 'utils/auth'
import { getOrUpdateDeviceId } from 'utils/device'
import { isInIframe, isTestEnv } from 'utils/env'
import { isGuest } from 'utils/user'

import { login, LOGIN_URL } from './api/auth'
import { API_CHANGES, isSyncAuthenticationError } from './api/changes'
import LocalStorage from './LocalStorage'

export function API_URL() {
  const picked = LocalStorage.get(LocalStorageKey.APP_HOST)

  const apiUrl =
    isTestEnv() && picked !== null && picked !== undefined
      ? picked
      : process.env.REACT_APP_API_URL

  return apiUrl
}

/**
 * Determines if a request should be retried based on the error
 */
function shouldRetry(error: AxiosError) {
  const httpCode = error.response?.status

  if (isInIframe() || isGuest() || error.config?.url?.includes(LOGIN_URL)) {
    return false
  }

  if (isPermissionOrSubscriptionError(error)) {
    return false
  }

  return (
    (httpCode === 401 || httpCode === 403) &&
    LocalStorage.get(LocalStorageKey.USER_ID) != undefined
  )
}

class Api {
  instance = axios.create({
    baseURL: `${API_URL()}/api`
  })

  constructor() {
    // Updated request interceptor for Axios 1.x
    this.instance.interceptors.request.use(
      (req: InternalAxiosRequestConfig) => {
        if (getAccessToken()) {
          const token = getAccessToken() as string
          const skipAuth = req.url?.includes(LOGIN_URL)

          if (!skipAuth) {
            req.headers.set('Authorization', token)
          }
        }
        return req
      }
    )

    // App version header, injected at build time; surfaced in backend logs and Sentry breadcrumbs
    this.instance.interceptors.request.use(
      (req: InternalAxiosRequestConfig) => {
        req.headers.set(
          'X-App-Version',
          `${process.env.APP_VERSION}-${process.env.REACT_APP_APP_TYPE}`
        )
        return req
      }
    )

    // Updated response interceptor for Axios 1.x
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (!shouldRetry(error)) {
          return Promise.reject(error)
        }

        // Make sure config exists and is typed correctly
        const originalRequest = error.config
        if (!originalRequest) {
          return Promise.reject(error)
        }

        // Delay original requests until authorization has been completed
        const refreshingUserId = LocalStorage.get(LocalStorageKey.USER_ID)
        try {
          // Let the caller distinguish rejected credentials from an outage.
          // The interactive login flow retains its existing error handling.
          const result = await login(undefined, true)
          if (result instanceof Error) throw result
          if (!result?.authToken) throw new Error('Invalid login response')

          // Create new request with updated token
          const token = getAccessToken() as string

          // Set the Authorization header properly for Axios 1.x
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = token
          }

          return axios.request(originalRequest)
        } catch (loginError) {
          // Background callers also need the existing sign-in transition on a
          // terminal rejection, but an outage must retain their local session.
          if (
            isSyncAuthenticationError(loginError) &&
            refreshingUserId === LocalStorage.get(LocalStorageKey.USER_ID) &&
            typeof window.processLogout === 'function'
          ) {
            await window.processLogout()
          }
          return Promise.reject(loginError)
        }
      }
    )

    // Configure axios-retry
    axiosRetry(this.instance, {
      retries: 3,
      retryCondition: (error) => {
        return shouldRetry(error)
      },
      retryDelay: (retryCount) => {
        return retryCount * 5000
      }
    })
  }

  public get(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    Logger.blue(`request to ${url}`)
    return this.instance.get(url, config)
  }

  public delete(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    Logger.blue(`request to ${url}`)
    return this.instance.delete(url, config)
  }

  public post(
    url: string,
    body: Record<string, string | number | boolean | object | undefined | null>,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const lastUpdate = LocalStorage.get(LocalStorageKey.LAST_UPDATE)

    const postBody = {
      ...body
    }

    if (lastUpdate && body['LastUpdate'] != null) {
      // If we have last update, meaning very first getchanges finished
      Object.assign(postBody, {
        DeviceChangeID: getOrUpdateDeviceId(),
        LastUpdate: url.includes(API_CHANGES)
          ? lastUpdate
          : `${dayjs().unix()}`,
        UserID: LocalStorage.get(LocalStorageKey.USER_ID)
      })
    }

    Logger.blue(`request to ${url}; with body`, postBody)
    return this.instance.post(url, postBody, config)
  }
}

export default new Api()

export { VALID_403_CODES } from 'utils/api/codes'
